import React from 'react';
import { Box } from '@mui/material';
import { MC_STATUS, type McStatus } from './tokens';
import {
  MetricCard as DsMetricCard,
  Overline,
  Panel as DsPanel,
  StatusBadge as DsStatusBadge,
  type StatusTone,
} from '../design-system/v2';

/**
 * Mission Control primitives — kept for existing imports, rendered through
 * the canonical design-system/v2 components.
 */

export const MC_TONE: Record<McStatus, StatusTone> = {
  success: 'success',
  running: 'info',
  failed: 'error',
  policy: 'policy',
  waiting: 'warning',
  queued: 'neutral',
  unknown: 'neutral',
};

export const StatusBadge: React.FC<{
  status: McStatus;
  label?: string;
  size?: 'sm' | 'md';
}> = ({ status, label, size = 'sm' }) => (
  <DsStatusBadge
    tone={MC_TONE[status]}
    label={label ?? MC_STATUS[status].label}
    size={size}
    pulse={status === 'running'}
  />
);

export const MetricCard: React.FC<{
  label: string;
  value: string | number;
  hint?: string;
  tone?: McStatus;
  onClick?: () => void;
}> = ({ label, value, hint, tone, onClick }) => (
  <DsMetricCard label={label} value={value} hint={hint} tone={tone ? MC_TONE[tone] : 'neutral'} onClick={onClick} />
);

export const SectionLabel: React.FC<{ children: React.ReactNode; action?: React.ReactNode }> = ({
  children,
  action,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25, gap: 2 }}>
    <Overline>{children}</Overline>
    {action}
  </Box>
);

export const Panel: React.FC<{
  children: React.ReactNode;
  title?: string;
  action?: React.ReactNode;
  dense?: boolean;
}> = ({ children, title, action, dense }) => (
  <DsPanel title={title} actions={action} dense={dense} divided={Boolean(title)}>
    {children}
  </DsPanel>
);
